import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, Sparkles, UserCircle2, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { pickLocalizedText } from '../utils/localizedContent';

const APP_LOGO_URL = process.env.REACT_APP_LOGO_URL || '/logo192.png';

export default function Auth() {
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({ identifier: '', email: '', password: '', name: '', lastName: '', username: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [popupVisible, setPopupVisible] = useState(false);
  const { login, register, loginWithGoogle, requestPasswordReset } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';
  const closeDurationMs = 220;
  const text = (es, en) => pickLocalizedText(lang, es, en);

  useEffect(() => {
    const id = requestAnimationFrame(() => setPopupVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !loading && !googleLoading) {
        closeAndNavigate('/');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loading, googleLoading]);

  const closeAndNavigate = (to) => {
    setPopupVisible(false);
    window.setTimeout(() => navigate(to), closeDurationMs);
  };

  const formatError = (err) => {
    if (!err.response) {
      return text('Error de conexión. Inténtalo de nuevo.', 'Connection error. Please try again.');
    }
    const status = err.response?.status;
    if (status === 429) {
      return text(
        'Demasiados intentos fallidos. Espera unos minutos e inténtalo de nuevo.',
        'Too many failed attempts. Please wait a few minutes and try again.',
      );
    }
    const detail = err.response?.data?.detail;
    if (!detail) return text('Algo salió mal.', 'Something went wrong.');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const email = form.email.trim().toLowerCase();
      const identifier = form.identifier.trim();
      if (tab === 'login') {
        await login(identifier, form.password);
      } else {
        if (!form.name.trim()) { setError(t('auth.name') + ' is required'); setLoading(false); return; }
        if (!form.username.trim()) { setError(text('Usuario', 'Username') + ' is required'); setLoading(false); return; }
        await register(email, form.password, form.name.trim(), form.lastName.trim(), form.username.trim());
      }
      closeAndNavigate(from);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setForgotSuccess('');
    setLoading(true);
    try {
      const email = forgotEmail.trim().toLowerCase();
      await requestPasswordReset(email);
      setForgotSuccess(text(
        'Si el correo existe, te enviamos instrucciones para restablecer tu contraseña.',
        'If the email exists, we sent password reset instructions.',
      ));
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    setPopupVisible(false);
    window.setTimeout(() => loginWithGoogle(), closeDurationMs);
  };

  return (
    <div className="min-h-screen bg-transparent relative overflow-hidden">
      <button
        type="button"
        aria-label={text('Cerrar ventana de acceso', 'Close auth popup')}
        onClick={() => closeAndNavigate('/')}
        className="absolute inset-0 bg-transparent"
      />
      <div className="absolute -top-20 -left-16 w-72 h-72 bg-[#C9A962]/10 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-24 -right-20 w-80 h-80 bg-[#2D8B70]/10 blur-3xl rounded-full pointer-events-none" />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={text('Ventana de inicio de sesión y registro', 'Login and register popup')}
          className={`w-full max-w-md rounded-3xl border border-[#C9A962]/30 bg-[#0E1C18]/95 shadow-[0_30px_80px_rgba(0,0,0,0.55)] p-6 sm:p-7 transform-gpu transition-all duration-300 ease-out ${
            popupVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          {/* Back button */}
          <div className="mb-5">
            <button
              type="button"
              onClick={() => closeAndNavigate('/')}
              className="inline-flex items-center gap-2 text-[#A8B5AE] hover:text-[#F5F0E6] transition-colors text-sm"
            >
              <ArrowLeft size={16} />{t('common.back')}
            </button>
          </div>
          {/* Logo */}
          <div className="text-center mb-7">
            <img src={APP_LOGO_URL} alt="El Rincón de Camo" className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-2 border-[#C9A962]/30" />
            <h1 className="text-2xl font-bold text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>El Rincón de Camo</h1>
            <p className="text-[#A8B5AE] text-sm mt-1">
              {tab === 'login'
                ? text('Accede con email o usuario', 'Sign in with email or username')
                : text('Crea tu cuenta para reservar y competir', 'Create your account to book and compete')}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex bg-[#0F1F1A]/80 backdrop-blur rounded-xl p-1 mb-5 border border-[#C9A962]/20">
            <button onClick={() => { setTab('login'); setError(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'login' ? 'bg-[#C9A962] text-[#0A1612]' : 'text-[#A8B5AE] hover:text-[#F5F0E6]'}`}
              data-testid="login-tab">{t('auth.login')}</button>
            <button onClick={() => { setTab('register'); setError(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'register' ? 'bg-[#C9A962] text-[#0A1612]' : 'text-[#A8B5AE] hover:text-[#F5F0E6]'}`}
              data-testid="register-tab">{t('auth.register')}</button>
          </div>

          <form onSubmit={forgotMode ? handleForgotSubmit : handleSubmit} className="space-y-4 bg-[#0F1F1A]/75 backdrop-blur border border-[#C9A962]/20 rounded-2xl p-5 shadow-[0_8px_30px_rgba(0,0,0,0.25)]">
            <div className="flex items-center gap-2 text-[#C9A962] text-xs uppercase tracking-[0.2em] font-semibold">
              <Sparkles size={14} />
              {forgotMode
                ? text('Recuperar acceso', 'Recover access')
                : (tab === 'login' ? text('Bienvenido', 'Welcome back') : text('Nueva cuenta', 'New account'))}
            </div>
            {!forgotMode && tab === 'register' && (
              <div>
                <label className="block text-sm text-[#A8B5AE] mb-1">{t('auth.name')}</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                  placeholder={t('auth.name')} required data-testid="name-input" />
              </div>
            )}
            {!forgotMode && tab === 'register' && (
              <div>
                <label className="block text-sm text-[#A8B5AE] mb-1">{text('Apellidos', 'Last name')}</label>
                <input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                  placeholder={text('Opcional', 'Optional')} data-testid="last-name-input" />
              </div>
            )}
            {!forgotMode && tab === 'register' && (
              <div>
                <label className="block text-sm text-[#A8B5AE] mb-1">{text('Usuario', 'Username')}</label>
                <div className="relative">
                  <UserCircle2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B5AE]" />
                  <input type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl pl-9 pr-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                    placeholder={text('tu_usuario', 'your_username')} required data-testid="username-input" />
                </div>
              </div>
            )}
            {forgotMode ? (
              <div>
                <label className="block text-sm text-[#A8B5AE] mb-1">{t('auth.email')}</label>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                  className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                  placeholder="correo@ejemplo.com" required data-testid="forgot-email-input" />
              </div>
            ) : tab === 'login' ? (
              <div>
                <label className="block text-sm text-[#A8B5AE] mb-1">{text('Correo o usuario', 'Email or username')}</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B5AE]" />
                  <input type="text" value={form.identifier} onChange={e => setForm(f => ({ ...f, identifier: e.target.value }))}
                    className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl pl-9 pr-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                    placeholder={text('correo@ejemplo.com o usuario', 'email@example.com or username')} required data-testid="identifier-input" />
                </div>
              </div>
            ) : (
            <div>
              <label className="block text-sm text-[#A8B5AE] mb-1">{t('auth.email')}</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                placeholder="correo@ejemplo.com" required data-testid="email-input" />
            </div>
            )}
            {!forgotMode && (
            <div>
              <label className="block text-sm text-[#A8B5AE] mb-1">{t('auth.password')}</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-3 pr-12 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
                  placeholder="••••••••" required data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8B5AE] hover:text-[#F5F0E6]" data-testid="toggle-password">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            )}
            {forgotSuccess && <p className="text-[#2D8B70] text-sm bg-[#2D8B70]/10 px-3 py-2 rounded-lg">{forgotSuccess}</p>}
            {error && <p className="text-[#C45B4A] text-sm bg-[#C45B4A]/10 px-3 py-2 rounded-lg" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#C9A962]/15"
              data-testid="auth-submit">
              {loading ? <span className="w-4 h-4 border-2 border-[#0A1612]/30 border-t-[#0A1612] rounded-full animate-spin"></span> : null}
              {forgotMode
                ? text('Enviar enlace de recuperación', 'Send recovery link')
                : (tab === 'login' ? t('auth.login') : t('auth.register'))}
            </button>
            {tab === 'login' && (
              <button
                type="button"
                className="w-full text-sm text-[#C9A962] hover:text-[#F5F0E6] transition-colors"
                onClick={() => {
                  setForgotMode((prev) => !prev);
                  setError('');
                  setForgotSuccess('');
                }}
              >
                {forgotMode ? text('Volver al inicio de sesión', 'Back to login') : t('auth.forgot_password')}
              </button>
            )}
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#C9A962]/20"></div>
            <span className="text-[#A8B5AE] text-xs">{t('auth.or')}</span>
            <div className="flex-1 h-px bg-[#C9A962]/20"></div>
          </div>

          <button onClick={handleGoogleLogin} disabled={googleLoading}
            className="w-full btn-secondary py-3 flex items-center justify-center gap-3 text-sm disabled:opacity-60"
            data-testid="google-login-btn">
            {googleLoading ? (
              <span className="w-4 h-4 border-2 border-[#F5F0E6]/30 border-t-[#F5F0E6] rounded-full animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/><path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/><path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/><path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/></svg>
            )}
            {googleLoading
              ? text('Redirigiendo...', 'Redirecting...')
              : t('auth.google_login')}
          </button>
        </div>
      </div>
    </div>
  );
}
